import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Paper, Typography, TextField, Button,
    Alert, CircularProgress, InputAdornment,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axiosInstance from "./Axiosinstance";

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [email, setEmail]     = useState("");
    const [error, setError]     = useState("");
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) { setError("Email is required"); return; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError("Enter a valid email"); return; }

        setLoading(true);
        setError("");
        try {
            await axiosInstance.post("/api/auth/forgot-password", { email });
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.message || "Something went wrong. Try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{
            minHeight: "100vh", display: "flex", alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(135deg, #f0f4ff 0%, #e8f5e9 100%)", px: 2,
        }}>
            <Paper elevation={0} sx={{
                width: "100%", maxWidth: 440, borderRadius: 4,
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 8px 40px rgba(0,0,0,0.08)", overflow: "hidden",
            }}>
                {/* Header */}
                <Box sx={{
                    background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                    px: 4, py: 4, textAlign: "center",
                }}>
                    <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700 }}>
                        Forgot Password?
                    </Typography>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", mt: 0.5 }}>
                        Enter your email and we'll send a reset link
                    </Typography>
                </Box>

                <Box sx={{ px: 4, py: 4, background: "#fff" }}>
                    {success ? (
                        <Box sx={{ textAlign: "center" }}>
                            <Box sx={{
                                width: 64, height: 64, borderRadius: "50%",
                                background: "#e8f5e9", display: "flex",
                                alignItems: "center", justifyContent: "center",
                                mx: "auto", mb: 2, fontSize: 32,
                            }}>✉️</Box>
                            <Typography variant="h6" fontWeight={700} mb={1}>Check your inbox</Typography>
                            <Typography variant="body2" color="text.secondary" mb={3}>
                                If <strong>{email}</strong> is registered, a password reset link has been sent.
                                Check your spam folder if you don't see it.
                            </Typography>
                            <Button
                                fullWidth variant="outlined"
                                onClick={() => navigate("/login")}
                                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
                            >
                                Back to Login
                            </Button>
                        </Box>
                    ) : (
                        <Box component="form" onSubmit={handleSubmit} noValidate>
                            {error && (
                                <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setError("")}>
                                    {error}
                                </Alert>
                            )}

                            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
                                Email Address
                            </Typography>
                            <TextField
                                fullWidth type="email" placeholder="you@example.com"
                                value={email} onChange={(e) => { setEmail(e.target.value); setError(""); }}
                                error={Boolean(error)} disabled={loading}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <EmailOutlinedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    mb: 3,
                                    "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "rgba(0,0,0,0.02)" },
                                }}
                            />

                            <Button
                                type="submit" fullWidth variant="contained" disabled={loading}
                                sx={{
                                    py: 1.5, borderRadius: 2, fontWeight: 600,
                                    textTransform: "none", fontSize: "1rem",
                                    background: "linear-gradient(135deg, #1976d2, #1565c0)",
                                    boxShadow: "0 4px 15px rgba(25,118,210,0.3)",
                                    mb: 2,
                                }}
                            >
                                {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Send Reset Link"}
                            </Button>

                            <Button
                                fullWidth variant="text"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate("/login")}
                                sx={{ textTransform: "none", fontWeight: 500, color: "text.secondary" }}
                            >
                                Back to Login
                            </Button>
                        </Box>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

export default ForgotPassword;
